import { useEffect, useState } from 'react'
import { Filter } from 'lucide-react'
import type { ReactNode } from 'react'

import type { ListFilterChip } from '@/components/common/list-filter-chips'

import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { ListFilterChips } from '@/components/common/list-filter-chips'
import { RefreshButton } from '@/components/common/refresh-button'
import { Button } from '@/components/ui/button'
import {
    Sheet,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { cn } from '@/lib/utils'
import { m } from '@/lib/paraglide/messages'

export type { ListFilterChip }

export interface ListFilterBarProps<T> {
    /** Bộ lọc đang áp dụng — thường chính là URL search params (R9). */
    value: T
    /**
     * Ghi bộ lọc mới. Desktop gọi ngay khi người dùng đổi; mobile chỉ gọi một
     * lần khi bấm "Áp dụng", để không bắn N request trong lúc sheet che danh sách.
     */
    onApply: (next: T) => void
    /** Đưa mọi bộ lọc về mặc định (R4). */
    onClear: () => void
    /** Số bộ lọc đang khác mặc định → badge trên nút Lọc (R3). */
    activeCount: number
    /**
     * Nút "Xoá lọc" bật hay mờ. Mặc định theo `activeCount` (chỉ đếm bộ lọc
     * trong sheet). Màn muốn Xoá lọc gỡ cả từ khoá tìm kiếm (M-149) truyền
     * `activeCount > 0 || !!q` — badge trên nút Lọc vẫn không đếm ô tìm kiếm.
     */
    clearActive?: boolean
    /**
     * Ô tìm kiếm. Luôn hiện ngoài, KHÔNG gom vào sheet vì đây là thao tác dùng
     * nhiều nhất (R2). Tự quản state + debounce, không đi qua bản nháp.
     */
    search?: ReactNode
    /** Chip bộ lọc đang bật, gỡ được từng cái (R12). */
    chips?: ListFilterChip[]
    /**
     * Bấm "Làm mới" → nạp lại danh sách. Không truyền thì nút không render:
     * màn nào không có `refetch` để gắn thì thà không có nút còn hơn có một
     * nút bấm vào không xảy ra gì.
     */
    onRefresh?: () => void
    /** Query đang chạy lại → nút khoá và icon quay (R3). */
    refreshing?: boolean
    /** Số cột của lưới desktop. */
    columns?: 3 | 4
    /**
     * Chỉ dựng hàng mobile + sheet. Dành cho màn đã có cột lọc riêng ở desktop
     * (ListFilterSidebarLayout): lưới desktop và chip ẩn bằng CSS vẫn nằm trong
     * DOM sẽ nhân đôi control và chip.
     */
    mobileOnly?: boolean
    /**
     * Các bộ lọc còn lại. Nhận `value` (bản nháp khi đang ở trong sheet) và
     * `patch` để ghi. Không truyền → màn chỉ có ô tìm kiếm, không render nút Lọc.
     */
    children?: (ctx: {
        value: T
        patch: (partial: Partial<T>) => void
        /**
         * Bề mặt đang render — cùng một hàm `children` được gọi cho CẢ lưới
         * desktop lẫn sheet, nên control cần biết để chọn chiều cao (R8).
         */
        surface: 'bar' | 'sheet'
    }) => ReactNode
}

const COLUMN_CLASS: Record<3 | 4, string> = {
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
}

/**
 * Thanh lọc dùng chung cho mọi màn danh sách.
 *
 * Desktop (≥ md): lưới inline, đổi là áp dụng ngay.
 * Mobile (< md): ô tìm kiếm + nút Lọc có badge đếm + nút Làm mới chỉ-icon;
 * phần còn lại nằm trong bottom sheet giữ bản nháp, bấm "Áp dụng" mới ghi.
 *
 * Breakpoint cố định ở `md` (768px) để trùng chỗ bảng ↔ card đổi (R1).
 */
export function ListFilterBar<T>({
    value,
    onApply,
    onClear,
    activeCount,
    clearActive = activeCount > 0,
    search,
    chips,
    onRefresh,
    refreshing,
    columns = 4,
    mobileOnly = false,
    children,
}: ListFilterBarProps<T>) {
    const isMobile = useIsMobile()
    const [sheetOpen, setSheetOpen] = useState(false)
    const [draft, setDraft] = useState<T>(value)
    // Chỉ những ô người dùng thực sự chạm vào trong sheet. Áp dụng sẽ ghi
    // `{...value, ...touched}` chứ không ghi cả `draft` — vì `draft` là ảnh
    // chụp lúc mở sheet, ghi đè cả nó sẽ xoá mất thứ vừa đổi từ bên ngoài
    // (điển hình: ô tìm kiếm có debounce nhả giá trị sau khi sheet đã mở).
    const [touched, setTouched] = useState<Partial<T>>({})

    // Bản nháp chỉ sống trong lúc sheet mở. Mở lại (hoặc bộ lọc đổi từ nơi
    // khác, vd nút gỡ chip) thì nạp lại từ giá trị đang áp dụng — nhờ vậy
    // đóng sheet giữa chừng là bản nháp bị bỏ, không rò rỉ sang lần sau.
    useEffect(() => {
        if (sheetOpen) return
        setDraft(value)
        setTouched({})
    }, [sheetOpen, value])

    // Qua mốc md, nội dung sheet bị `md:hidden` giấu đi nhưng lớp phủ của
    // radix thì không, và body vẫn bị khoá `pointer-events: none` → cả trang
    // tối om, bấm không được. Xoay ngang tablet là dính. Đóng sheet luôn.
    useEffect(() => {
        if (!isMobile) setSheetOpen(false)
    }, [isMobile])

    const patchLive = (partial: Partial<T>) => onApply({ ...value, ...partial })
    const patchDraft = (partial: Partial<T>) => {
        setDraft((prev) => ({ ...prev, ...partial }))
        setTouched((prev) => ({ ...prev, ...partial }))
    }

    const body = (
        <div className="space-y-3">
            {/* Mobile: ô tìm kiếm + nút Lọc + Làm mới */}
            <div className="flex items-end gap-2 md:hidden">
                {search ? <div className="min-w-0 flex-1">{search}</div> : null}
                {children ? (
                    // SheetTrigger (không phải onClick tay) để radix tự trả
                    // focus về đúng nút này khi sheet đóng — người dùng bàn
                    // phím không bị rơi focus về đầu trang.
                    <SheetTrigger asChild>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 shrink-0"
                            data-testid="list-filter-trigger"
                            aria-label={m.common_filter()}
                        >
                            <Filter className="size-4" />
                            {m.common_filter()}
                            {activeCount > 0 ? (
                                <span className="bg-primary text-primary-foreground ml-1 rounded-full px-1.5 text-xs font-medium tabular-nums">
                                    {activeCount}
                                </span>
                            ) : null}
                        </Button>
                    </SheetTrigger>
                ) : null}
                {onRefresh ? (
                    // Chỉ icon: hàng này đã có ô tìm kiếm và nút Lọc có chữ,
                    // thêm một nhãn nữa là tràn trên máy 390px. Tên nút vẫn
                    // đọc được qua aria-label.
                    <RefreshButton
                        onClick={onRefresh}
                        loading={refreshing}
                        iconOnly
                        className="shrink-0"
                    />
                ) : null}
            </div>

            {mobileOnly ? null : (
                <>
                    {/* Desktop: lưới inline */}
                    <div
                        data-testid="list-filter-desktop"
                        className={cn(
                            'hidden gap-3 md:grid',
                            COLUMN_CLASS[columns],
                        )}
                    >
                        {search}
                        {children?.({
                            value,
                            patch: patchLive,
                            surface: 'bar',
                        })}
                        {/* flex-wrap: hai nút cộng lại rộng ~187px, hơn một ô lưới ở
                    768–1024px. Không cho xuống dòng thì nút thứ hai tràn khỏi ô
                    và bị mép lưới cắt mất chữ (màn Kiểm kê). */}
                        <div className="flex flex-wrap items-end gap-2">
                            {onRefresh ? (
                                <RefreshButton
                                    onClick={onRefresh}
                                    loading={refreshing}
                                />
                            ) : null}
                            <ClearFilterButton
                                active={clearActive}
                                onClear={onClear}
                            />
                        </div>
                    </div>
                    {/* Chip bộ lọc đang bật — chung cho cả hai bố cục */}
                    <ListFilterChips chips={chips ?? []} />
                </>
            )}

            {children ? (
                <SheetContent
                    side="bottom"
                    data-testid="list-filter-sheet"
                    aria-describedby={undefined}
                    className="md:hidden"
                >
                    <SheetHeader>
                        <SheetTitle>{m.common_filter()}</SheetTitle>
                    </SheetHeader>
                    <div className="grid content-start gap-3 overflow-y-auto">
                        {children({
                            value: draft,
                            patch: patchDraft,
                            surface: 'sheet',
                        })}
                    </div>
                    <SheetFooter>
                        <ClearFilterButton
                            active={clearActive}
                            onClear={onClear}
                            size="default"
                            className="h-11 flex-1"
                        />
                        <Button
                            type="button"
                            className="h-11 flex-1"
                            data-testid="list-filter-apply"
                            onClick={() => {
                                onApply({ ...value, ...touched })
                                setSheetOpen(false)
                            }}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </SheetFooter>
                </SheetContent>
            ) : null}
        </div>
    )

    if (!children) return body
    return (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            {body}
        </Sheet>
    )
}
